import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdAdd, MdHorizontalRule } from 'react-icons/md';
import ContentText from '../atoms/ContentText';
import { BREAKPOINT_SP } from '@/util/const';

type AccordionProps = {
  children: React.ReactNode;
  title: React.ReactNode;
  isOpen?: boolean;
  initOpen?: boolean;
  onClick?: (state: boolean) => void;
  className?: string; //for container
  headerClassName?: string; // for the header
  contentClassName?: string; // for the content
  disableToggle?: boolean;
};

const Accordion: React.FC<AccordionProps> = ({
  children,
  title,
  isOpen,
  initOpen = false,
  onClick,
  className,
  headerClassName,
  contentClassName,
  disableToggle = false,
}) => {
  const [open, setOpen] = useState(initOpen);

  useEffect(() => {
    if (isOpen !== undefined) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disableToggle) return;
    const newState = !open;
    setOpen(newState);
    if (onClick) {
      onClick(newState);
    }
  };

  const isSimpleText = (child: React.ReactNode): boolean => {
    return typeof child === 'string' || typeof child === 'number';
  };

  return (
    <AccordionContainer className={`${className ?? ''} ${open ? 'open' : ''}`}>
      <AccordionHeader className={headerClassName} onClick={handleToggle}>
        <ContentText type="article-l-b">{title}</ContentText>
        {!disableToggle &&
          (open ? <MdHorizontalRule size="24px" /> : <MdAdd size="24px" />)}
      </AccordionHeader>
      <AccordionContent
        className={`${contentClassName ?? ''} ${open ? 'open' : 'closed'}`}
      >
        {isSimpleText(children) ? (
          <ContentText type="article-l" className="children">
            {children}
          </ContentText>
        ) : (
          <div className="children">{children}</div> // Directly render children if they are not simple text
        )}
      </AccordionContent>
    </AccordionContainer>
  );
};

export default Accordion;

const AccordionContainer = styled.div`
  /* font-family: 'Yu Mincho'; */
  border-top: 1px solid var(--gray-300-border, #d4d4d8);
  border-bottom: 1px solid var(--gray-300-border);
  padding: 24px;
  &:has(+ div) {
    border-bottom: none;
  }
  @media (max-width: ${BREAKPOINT_SP}) {
    padding: 12px;
  }
`;

const AccordionHeader = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const AccordionContent = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  padding-top: 0;
  transition: all 0.3s ease-out;

  &:not(:last-child) {
    border-bottom: none;
  }
  &.open {
    padding-top: 24px;
    grid-template-rows: 1fr;
  }
  &.closed {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border: 0 solid transparent !important;
  }

  > .children {
    overflow: hidden;
    margin: 0 12px;
    color: var(--gray-600-font-subcolor);
  }

  @media (max-width: ${BREAKPOINT_SP}) {
    &.open {
      padding-top: 12px;
    }
    > .children {
      margin: 0 8px;
    }
  }
`;
